import { useEffect, useState } from 'react';
import { Tab, TabGroup } from '@humanforce/mango-ui-react';
import { Navigate, Route, Routes, useNavigate, useParams, useSearchParams } from 'react-router';
import { PageTitle } from '../components/layout/PageTitle';
import { OrgSwitcher } from '../features/organisation/components/OrgSwitcher';
import { OverviewTab } from '../features/organisation/tabs/OverviewTab';
import { BusinessRecordsTab } from '../features/organisation/tabs/BusinessRecordsTab';
import { LocationsTab } from '../features/organisation/tabs/LocationsTab';
import { PayrollTab } from '../features/organisation/tabs/PayrollTab';
import { BankingTab } from '../features/organisation/tabs/BankingTab';
import { ComplianceTab } from '../features/organisation/tabs/ComplianceTab';
import { PublicHolidaysTab } from '../features/organisation/tabs/PublicHolidaysTab';
import { PayAdviceTab } from '../features/organisation/tabs/PayAdviceTab';
import { listOrganisations, getOrganisationExtra } from '../features/organisation/organisationApi';
import { loadOrganisationDetails } from '../features/organisation/organisationDetailsApi';
import { ORG_ORDER } from '../features/organisation/organisationFixtures';
import type { OrganisationPermissions } from '../features/organisation/organisationDetailsModel';
import type { OrgData, OrgExtra } from '../features/organisation/organisationFixtures';

const TABS = [
  { slug: 'overview',         label: 'Overview' },
  { slug: 'business-records', label: 'Business records' },
  { slug: 'locations',        label: 'Locations' },
  { slug: 'payroll',          label: 'Payroll' },
  { slug: 'banking',          label: 'Banking' },
  { slug: 'compliance',       label: 'Compliance' },
  { slug: 'public-holidays',  label: 'Public holidays' },
  { slug: 'pay-advice',       label: 'Pay advice' },
] as const;

const VALID_TABS = TABS.map((t) => t.slug);

export const OrganisationPage = () => {
  const { '*': wildcardTabParam, tab: namedTabParam } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const [orgs, setOrgs] = useState<Record<string, OrgData>>({});
  const [orgExtra, setOrgExtra] = useState<OrgExtra | undefined>();
  const [permissions, setPermissions] = useState<OrganisationPermissions | undefined>();
  const [orgsError, setOrgsError] = useState<string | null>(null);
  const [extraError, setExtraError] = useState<string | null>(null);

  const activeOrgId = searchParams.get('org') ?? ORG_ORDER[0];
  const permissionsScenario = searchParams.get('scenario') === 'permissionDenied' ? 'permissionDenied' : 'default';
  const tabParam = wildcardTabParam ?? namedTabParam;
  const activeTab = VALID_TABS.includes(tabParam as (typeof VALID_TABS)[number]) ? (tabParam as string) : 'overview';

  useEffect(() => {
    (async () => {
      try {
        const { orgs: data } = await listOrganisations();
        setOrgs(data);
      } catch {
        setOrgsError('Failed to load organisations. Please refresh the page.');
      }
    })();
  }, []);

  useEffect(() => {
    let ignore = false;
    setOrgExtra(undefined);
    setExtraError(null);
    (async () => {
      try {
        const data = await getOrganisationExtra(activeOrgId);
        if (!ignore) setOrgExtra(data);
      } catch {
        if (!ignore) setExtraError('Failed to load organisation details.');
      }
    })();
    return () => { ignore = true; };
  }, [activeOrgId]);

  useEffect(() => {
    let ignore = false;
    setPermissions(undefined);
    (async () => {
      try {
        const { permissions: nextPermissions } = await loadOrganisationDetails(permissionsScenario);
        if (!ignore) setPermissions(nextPermissions);
      } catch {
        if (!ignore) setPermissions({ canEdit: false, canEditRestrictedStatus: false, canView: false });
      }
    })();
    return () => { ignore = true; };
  }, [permissionsScenario]);

  const switchOrg = (orgId: string) => {
    const next = new URLSearchParams(searchParams);
    next.set('org', orgId);
    setSearchParams(next);
  };

  const switchTab = (slug: string) => {
    const params = new URLSearchParams(searchParams);
    navigate(`/maintenance/organisation/${slug}?${params.toString()}`);
  };

  const activeOrgData = orgs[activeOrgId];

  if (orgsError)
    return <p style={{ color: 'var(--mgo-color-fg-negative-primary)', padding: 'var(--mgo-space-40)' }}>{orgsError}</p>;

  const extraErrorPanel = extraError
    ? <p style={{ color: 'var(--mgo-color-fg-negative-primary)' }}>{extraError}</p>
    : null;

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 'var(--mgo-space-40)', flexWrap: 'wrap', marginBottom: 'var(--mgo-space-40)' }}>
        <PageTitle title="Organisation" />
        {Object.keys(orgs).length > 0 && (
          <OrgSwitcher orgIds={Object.keys(orgs)} orgs={orgs} activeOrgId={activeOrgId} onChange={switchOrg} />
        )}
      </div>
      <TabGroup value={activeTab} onValueChange={switchTab}>
        <Tab value="overview" label="Overview">
          {activeOrgData && permissions ? <OverviewTab key={activeOrgId} orgId={activeOrgId} orgData={activeOrgData} permissions={permissions} /> : null}
        </Tab>
        <Tab value="business-records" label="Business records">
          {activeOrgData ? <BusinessRecordsTab key={activeOrgId} orgId={activeOrgId} orgData={activeOrgData} /> : null}
        </Tab>
        <Tab value="locations" label="Locations">
          {activeOrgData ? <LocationsTab orgId={activeOrgId} orgData={activeOrgData} /> : null}
        </Tab>
        <Tab value="payroll" label="Payroll">
          {activeOrgData ? <PayrollTab orgId={activeOrgId} orgData={activeOrgData} /> : null}
        </Tab>
        <Tab value="banking" label="Banking">
          {extraErrorPanel ?? (activeOrgData ? <BankingTab orgId={activeOrgId} orgData={activeOrgData} orgExtra={orgExtra} /> : null)}
        </Tab>
        <Tab value="compliance" label="Compliance">
          {activeOrgData ? <ComplianceTab orgId={activeOrgId} orgData={activeOrgData} /> : null}
        </Tab>
        <Tab value="public-holidays" label="Public holidays">
          {activeOrgData ? <PublicHolidaysTab orgId={activeOrgId} orgData={activeOrgData} /> : null}
        </Tab>
        <Tab value="pay-advice" label="Pay advice">
          {extraErrorPanel ?? (activeOrgData ? <PayAdviceTab orgId={activeOrgId} orgData={activeOrgData} orgExtra={orgExtra} /> : null)}
        </Tab>
      </TabGroup>
      <Routes>
        <Route index element={<Navigate to={{ pathname: 'overview', search: searchParams.toString() ? `?${searchParams.toString()}` : '' }} replace />} />
      </Routes>
    </>
  );
};
